import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, ChevronDown, Globe, Instagram, Landmark, Mail, MapPin, Phone, ShieldCheck } from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;
export const LIME = '#C7FA3E';

export const utm = (url, page, slug, action) => {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}utm_source=ninjathlete&utm_medium=referral&utm_campaign=recovery_locator&utm_content=${page}_${slug}_${action}`;
};

export const trackClick = (slug, page, kind, href, label) => {
  fetch(`${API}/api/directory/track-click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gym_id: slug, directory: `locator:${page}`, kind, href: (href || '').slice(0, 300), label: (label || '').slice(0, 80) }),
    keepalive: true,
  }).catch(() => {});
};

export const trackLocator = (event, extra = {}) => {
  fetch(`${API}/api/locator/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, ...extra }),
    keepalive: true,
  }).catch(() => {});
};

export const useLocatorPageView = (key) => {
  useEffect(() => { trackLocator('page_view', { page: window.location.pathname }); }, [key]);
};

export const useJsonLd = (id, schema) => {
  useEffect(() => {
    if (!schema) return undefined;
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = id;
    s.textContent = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => { if (s.parentNode) s.parentNode.removeChild(s); };
  }, [id, schema]);
};

export const SEGMENT_LABELS = {
  recovery_iv_peptide: 'Recovery & IV/Peptide Clinic',
  med_spa_wellness: 'Med Spa & Wellness',
  college_sports_perf: 'Sports Performance',
  gym_biohacking: 'Gym & Biohacking Club',
};

const THERAPY_LABELS = {
  'red-light-therapy': 'Red Light', 'cold-plunge': 'Cold Plunge', 'infrared-sauna': 'Infrared Sauna',
  'iv-therapy': 'IV Therapy', 'compression-therapy': 'Compression', 'cryotherapy': 'Cryotherapy',
  'bodywork': 'Bodywork', 'peptide-therapy': 'Peptides', 'traditional-sauna': 'Sauna',
  'med-spa': 'Med Spa', 'hyperbaric-oxygen': 'HBOT', 'hormone-therapy': 'Hormones/TRT',
  'contrast-therapy': 'Contrast', 'sports-performance': 'Performance', 'nad-iv': 'NAD+',
  'medical-weight-loss': 'Weight Loss', 'float-therapy': 'Float', 'pemf-therapy': 'PEMF',
  'regenerative-medicine': 'PRP/Regen', 'oxygen-therapy': 'Oxygen', 'ozone-therapy': 'Ozone',
  'halotherapy': 'Salt Therapy', 'longevity-medicine': 'Longevity Med', 'vo2-testing': 'VO2/Testing',
  'breathwork': 'Breathwork',
};
export const therapyLabel = (slug) => THERAPY_LABELS[slug] || slug;

export const lcTherapy = (name) => name.toLowerCase()
  .replace(/\biv\b/g, 'IV').replace(/\bhbot\b/g, 'HBOT').replace(/\bnad\+?/g, 'NAD+')
  .replace(/\bpemf\b/g, 'PEMF').replace(/\btrt\b/g, 'TRT').replace(/\bprp\b/g, 'PRP')
  .replace(/\bglp-1\b/g, 'GLP-1').replace(/\bvo2\b/g, 'VO2');

export const Eyebrow = ({ children }) => (
  <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>{children}</p>
);

export const StatBar = ({ stats, testPrefix }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-8" data-testid={`${testPrefix}-stat-bar`}>
    {stats.map(([value, label]) => (
      <div key={label} className="bg-zinc-900/90 border border-zinc-800 p-4 text-center">
        <p className="text-[#C7FA3E] text-2xl md:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{value}</p>
        <p className="text-zinc-500 text-[11px] uppercase tracking-wider mt-1">{label}</p>
      </div>
    ))}
  </div>
);

export const JumpChips = ({ chips, testPrefix }) => (
  <div className="flex flex-wrap gap-2 my-6" data-testid={`${testPrefix}-jump-nav`}>
    {chips.map(([href, label]) => (
      <a key={href} href={href} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors">
        {label}
      </a>
    ))}
  </div>
);

export const FacilityCard = ({ facility: f, page }) => (
  <div id={f.slug} className="bg-zinc-900/90 border border-zinc-800 hover:border-[#C7FA3E]/40 p-5 transition-colors scroll-mt-28" data-testid={`facility-card-${f.slug}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h3 className="text-white text-lg leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>{f.name}</h3>
        <p className="text-zinc-500 text-xs flex items-center gap-1.5 mt-1 flex-wrap" data-testid={`facility-address-${f.slug}`}>
          <MapPin className="w-3 h-3 shrink-0" /> {f.street_address ? `${f.street_address}, ` : ''}{f.city}{f.state ? `, ${f.state}` : ''}
          <span className="text-zinc-700">·</span>
          <span>{SEGMENT_LABELS[f.segment] || 'Recovery Facility'}</span>
        </p>
      </div>
      {(f.tier === 'claimed' || f.tier === 'featured') && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#C7FA3E]/10 text-[#C7FA3E] border border-[#C7FA3E]/30">
          <BadgeCheck className="w-3 h-3" /> Claimed
        </span>
      )}
    </div>
    <div className="flex flex-wrap gap-1.5 my-3">
      {f.therapies.slice(0, 6).map((t) => (
        <span key={t} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700">{therapyLabel(t)}</span>
      ))}
      {f.therapies.length > 6 && <span className="px-2 py-0.5 text-[10px] text-zinc-500">+{f.therapies.length - 6} more</span>}
    </div>
    {f.description && <p className="text-zinc-400 text-sm leading-relaxed mb-4">{f.description}</p>}
    <div className="flex flex-wrap items-center gap-2">
      {f.website && (
        <a
          href={utm(f.website, page, f.slug, 'website')}
          target="_blank" rel="noopener noreferrer"
          onClick={() => trackClick(f.slug, page, 'website', f.website, f.name)}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#C7FA3E] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
          style={{ fontFamily: 'Oswald, sans-serif' }}
          data-testid={`facility-website-${f.slug}`}
        >
          Visit Website <Globe className="w-3 h-3" />
        </a>
      )}
      {f.phone && (
        <a href={`tel:${f.phone}`} onClick={() => trackClick(f.slug, page, 'phone', f.phone, f.name)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 text-zinc-300 text-xs hover:border-zinc-500 transition-colors">
          <Phone className="w-3 h-3" /> {f.phone}
        </a>
      )}
      {f.instagram && (
        <a
          href={`https://www.instagram.com/${f.instagram.replace('@', '')}/`}
          target="_blank" rel="noopener noreferrer"
          onClick={() => trackClick(f.slug, page, 'social', f.instagram, f.name)}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 text-zinc-300 text-xs hover:border-zinc-500 transition-colors"
        >
          <Instagram className="w-3 h-3" /> {f.instagram}
        </a>
      )}
    </div>
    <Link to={`/locator/claim/${f.slug}`} onClick={() => trackLocator('claim_click', { slug: f.slug, page })} className="inline-block mt-3 text-zinc-400 hover:text-[#C7FA3E] text-[11px] underline underline-offset-2 transition-colors" data-testid={`facility-claim-${f.slug}`}>
      Own this facility? Claim this listing — add pricing, photos &amp; a booking link
    </Link>
    {f.profile && (
      <Link to={`/locator/facility/${f.slug}`} className="block mt-3 text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline" data-testid={`facility-profile-link-${f.slug}`}>
        Full profile, services &amp; pricing →
      </Link>
    )}
  </div>
);

export const CompactRow = ({ facility: f, page }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 border-b border-zinc-800/70" data-testid={`facility-row-${f.slug}`}>
    <div className="flex-1 min-w-[200px]">
      <span className="text-white text-sm font-medium">{f.name}</span>
      <span className="text-zinc-600 text-xs ml-2">{f.city}{f.state ? `, ${f.state}` : ''}</span>
    </div>
    <div className="hidden md:flex flex-wrap gap-1">
      {f.therapies.slice(0, 4).map((t) => (
        <span key={t} className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-zinc-800 text-zinc-500">{therapyLabel(t)}</span>
      ))}
    </div>
    {f.website && (
      <a href={utm(f.website, page, f.slug, 'website')} target="_blank" rel="noopener noreferrer" onClick={() => trackClick(f.slug, page, 'website', f.website, f.name)} className="text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline inline-flex items-center gap-1">
        Website <ArrowRight className="w-3 h-3" />
      </a>
    )}
  </div>
);

export const InstitutionalBlock = ({ facilities, testPrefix }) => (
  facilities.length === 0 ? null : (
    <section className="mt-12 border border-zinc-800 bg-zinc-900/60 p-6" data-testid={`${testPrefix}-institutional`}>
      <div className="flex items-center gap-2 mb-2">
        <Landmark className="w-4 h-4 text-[#C7FA3E]" />
        <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Where the Pros Recover</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-4">University athletic departments and elite training centers in this area running the same recovery modalities — listed for reference, not open to the public.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {facilities.map((f) => (
          <div key={f.slug} className="border border-zinc-800 p-3">
            <p className="text-white text-sm font-medium">{f.name}</p>
            <p className="text-zinc-600 text-xs mt-0.5">{f.city}{f.state ? `, ${f.state}` : ''}{f.description ? ` — ${f.description}` : ''}</p>
          </div>
        ))}
      </div>
    </section>
  )
);

export const FaqBlock = ({ faqs, testPrefix }) => {
  const [open, setOpen] = useState(0);
  return (
    <section id="locator-faq" className="mt-12 scroll-mt-28" data-testid={`${testPrefix}-faq`}>
      <h2 className="text-white text-3xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Frequently Asked Questions</h2>
      <div className="space-y-2">
        {faqs.map((f, i) => (
          <div key={f.question} className="border border-zinc-800 bg-zinc-900/60">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left" data-testid={`${testPrefix}-faq-q-${i}`}>
              <span className="text-white text-sm font-medium">{f.question}</span>
              <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && <p className="px-4 pb-4 text-zinc-400 text-sm leading-relaxed">{f.answer}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

export const EmailCapture = ({ metro, metroName, testPrefix }) => {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const res = await fetch(`${API}/api/locator/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, metro }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      setErr('Something went wrong — double-check your email and try again.');
    }
  };
  return (
    <div className="border border-[#C7FA3E]/25 bg-[#C7FA3E]/[0.04] p-5 my-8" data-testid={`${testPrefix}-email-capture`}>
      {done ? (
        <p className="text-[#C7FA3E] text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> You're on the list — watch your inbox for new clinics and deals.</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex-1">
            <p className="text-white text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-[#C7FA3E]" /> Get {metroName || 'local'} recovery deals + new clinic alerts</p>
          </div>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="bg-black border border-zinc-700 focus:border-[#C7FA3E] outline-none text-white text-sm px-3 py-2.5 w-full sm:w-60 placeholder:text-zinc-600"
            data-testid={`${testPrefix}-email-input`}
          />
          <button type="submit" className="px-5 py-2.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid={`${testPrefix}-email-submit`}>
            Notify Me
          </button>
        </form>
      )}
      {err && !done && <p className="text-[#E63946] text-xs mt-2" data-testid={`${testPrefix}-email-error`}>{err}</p>}
    </div>
  );
};

export const MetroGrid = ({ metros, title = 'Recovery Directories in More Cities', testPrefix }) => (
  <section className="mt-12" data-testid={`${testPrefix}-metro-grid`}>
    <h2 className="text-white text-3xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{title}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {metros.map((m) => (
        <Link key={m.slug} to={`/locator/${m.slug}`} className="border border-zinc-800 hover:border-[#C7FA3E]/40 bg-zinc-900/60 p-4 transition-colors group" data-testid={`metro-link-${m.slug}`}>
          <p className="text-white group-hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>{m.name}</p>
          <p className="text-zinc-500 text-xs mt-1">{m.count} facilities{m.top_therapies?.length ? ` · ${m.top_therapies.join(' · ')}` : ''}</p>
        </Link>
      ))}
    </div>
  </section>
);

export const localBusinessList = (facilities, pageUrl, pageName) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: pageName,
  url: `https://ninjathlete.com${pageUrl}`,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: facilities.slice(0, 50).map((f, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': f.segment === 'recovery_iv_peptide' ? 'MedicalClinic' : 'HealthAndBeautyBusiness',
        name: f.name,
        address: { '@type': 'PostalAddress', ...(f.street_address ? { streetAddress: f.street_address } : {}), addressLocality: f.city, addressRegion: f.state, addressCountry: 'US' },
        ...(f.phone ? { telephone: f.phone } : {}),
        ...(f.website ? { url: f.website } : {}),
        makesOffer: f.therapies.map((t) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: therapyLabel(t) } })),
      },
    })),
  },
});

export const LocatorLoading = () => (
  <div className="min-h-[60vh] bg-black flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-zinc-700 border-t-[#C7FA3E] rounded-full animate-spin" />
  </div>
);
